import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { makePostRequest } from "../utils/utils";
import swal from "sweetalert";

export default function ExcelToTable({ onImportComplete, importError }) {
  const [, setData] = useState([]);
  const [, setColumns] = useState([]);
  // const [error, setError] = useState(null);
  const [, setImporting] = useState(false);
  const [, setResults] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [filename, setfilename] = useState(false);

  const REQUIRED_HEADERS = [
    "first name",
    "last name",
    "emailid",
    "contactno",
    "age",
    "family members count",
    "apartments owned",
  ];

  const validateHeaders = (found) => {
    const norm = found.map((h) => h.trim().toLowerCase());
    const missing = REQUIRED_HEADERS.filter((r) => !norm.includes(r));
    const extra = norm.filter((h) => !REQUIRED_HEADERS.includes(h));
    if (missing.length || extra.length) {
      let msg = "";
      if (missing.length) msg += `Missing: ${missing.join(", ")}. `;
      if (extra.length) msg += `Unexpected: ${extra.join(", ")}.`;
      return msg;
    }
    return null;
  };

  const handleFileUpload = (e) => {
    // setError(null);
    importError(null)
    setData([]);
    setColumns([]);
    setResults(null);

    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fn = file.name.toLowerCase();
    setfilename(fn)

    const postProcess = (rows, foundHeaders) => {
      const norm = foundHeaders.map((h) => h.trim().toLowerCase());
      let hasResId = false;
      const cleanedHeaders = [];
      for (let i = 0; i < foundHeaders.length; i++) {
        if (norm[i] === "resident id") {
          hasResId = true;
          continue;
        }
        cleanedHeaders.push(foundHeaders[i]);
      }
      const cleanedRows = rows.map((r) => {
        const c = { ...r };
        if (hasResId) {
          delete c["Resident ID"];
          delete c["resident id"];
        }
        return c;
      });
      const errMsg = validateHeaders(cleanedHeaders);
      if (errMsg) {
        importError(`Header validation error: ${errMsg}`);
        return;
      }
      setColumns(cleanedHeaders);
      setData(cleanedRows);

      // confirm msg
      swal({
        title: `Import ${cleanedRows.length} Residents?`,
        text: "This will insert them into the database. Proceed?",
        icon: "warning",
        buttons: ["Cancel", "Import"],
        dangerMode: true,
      }).then((willImport) => {
        if (willImport) {
          handleImportResidents(cleanedRows);
        }
      });
    };

    if (fn.endsWith(".csv")) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => {
          if (res.errors.length) {
            const msgs = res.errors.map((err) => `Row ${err.row}: ${err.message}`);
            importError(msgs.join(" | "));
            return;
          }
          const rows = res.data;
          if (!rows.length) {
            importError("CSV has no data rows.");
            return;
          }
          const foundHeaders = res.meta.fields || [];
          postProcess(rows, foundHeaders);
        },
        error: (err) => {
          importError(`CSV parse error: ${err.message}`);
        },
      });
    } else if (fn.endsWith(".xls") || fn.endsWith(".xlsx")) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const bstr = ev.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const firstSheet = wb.SheetNames[0];
        const ws = wb.Sheets[firstSheet];
        const jsonData = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!jsonData.length) {
          importError("Excel sheet is empty.");
          return;
        }
        const foundHeaders = Object.keys(jsonData[0]);
        postProcess(jsonData, foundHeaders);
      };
      reader.readAsBinaryString(file);
    } else {
      importError("Unsupported file. Please upload .csv, .xls, or .xlsx");
    }


  };

  // Convert each “row object” into exactly the fields your ResidentDto expects:
  const rowToDto = (row) => {
    const getVal = (k) => {
      if (row.hasOwnProperty(k)) return row[k];
      const lk = k.trim().toLowerCase();
      for (const a of Object.keys(row)) {
        if (a.trim().toLowerCase() === lk) return row[a];
      }
      return "";
    };
    const firstname = (getVal("first name") || "").toString().trim();
    const lastname = (getVal("last name") || "").toString().trim();
    const email = (getVal("emailid") || "").toString().trim();
    const contact = (getVal("contactno") || "").toString().trim();
    const ageVal = (getVal("age") || "").toString().trim();
    const famVal = (getVal("family members count") || "").toString().trim();
    const aptVal = (getVal("apartments owned") || "").toString().trim();

    return {
      society_id: localStorage.getItem("socityid") || "",
      user_obj: {
        first_name: firstname,
        last_name: lastname,
        age: parseInt(ageVal, 10),
        contact_no: contact,
        email_id: email,
        profile_pic: "",
        profile_mode: "public",
      },
      family_count: parseInt(famVal, 10),
      apartment_count: parseInt(aptVal, 10),
      apartment_id_list: {},
      wing_ids_list: {},
      building_ids_list: {},
      uniq_apt_name: {},
      user_type: "Owner",
      document_list: {},
      password: firstname + "@" + contact.slice(0, 4),
      emergency_contact: false,
      schema_name: sessionStorage.getItem("schema") || "",
      society_code: localStorage.getItem("societyCode") || "",
    };
  };


  const handleImportResidents = async (rowsToImport) => {
    if (!rowsToImport.length) {
      importError("No valid data to import.");
      return;
    }
    setImporting(true);
    importError(null);
    setResults(null);
    setShowLoader(true);

    const dtoArray = rowsToImport.map((rowObj) => rowToDto(rowObj));

    try {
      const response = await makePostRequest("resident/bulk-insert", dtoArray);

      setResults(response.data.data);
      swal("Success", response.data.message, "success", {
        timer: 1000,
        buttons: false,
      });
      if (typeof onImportComplete === 'function') {
        await onImportComplete();
      }
      setData([]);


    } catch (err) {
      console.log(err.response?.data?.message || err.message || err || "Unknown error");

      swal("Error", err.response?.data?.message || err.message || err || "Unknown error", "error", {
        timer: 1000,
        buttons: false,
      });
    } finally {
      setShowLoader(false);
      setImporting(false);
    }
  };



  return (
    <>

      <div className="imporDownloadBtnsMain">

        <a className="impBtns" href="/sample_resident_template.xlsx" download style={{ background: '#674bdd', color: '#fff' }}>
          Download Sample CSV
        </a>
        <button className="impBtns importfileName">
          Import File
          <input
            type="file"
            accept=".csv, .xls, .xlsx"
            onChange={handleFileUpload}
            style={{ marginBottom: 16 }}
          />
          <span>{filename}</span>
        </button>


      </div>


      {showLoader ? (
        <div className="loaderOter" id="loaderId">
          <div className="wrapper">
            <div className="circle"></div>
            <div className="circle"></div>
            <div className="circle"></div>
            <div className="shadow"></div>
            <div className="shadow"></div>
            <div className="shadow"></div>
            <span>Loading</span>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
